import React from "react";
import { Container, Badge, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet";
import AppNavbar from "../components/common/AppNavbar";

const AboutUs = () => {
  // Data for regions
  const regions = [
    { name: "Punjab", icon: "🌾", description: "Land of five rivers" },
    { name: "Gujarat", icon: "🦁", description: "Cultural vibrance" },
    { name: "Sindhu", icon: "🌊", description: "Indus Valley legacy" },
    { name: "Maratha", icon: "⚔️", description: "Warrior spirit" },
    { name: "Dravida", icon: "🏛️", description: "Ancient temple architecture" },
    { name: "Utkala", icon: "🎨", description: "Artistic excellence" },
    { name: "Banga", icon: "🐅", description: "Intellectual renaissance" },
  ];

  // Data for rivers
  const rivers = [
    "Ganga",
    "Yamuna",
    "Godavari",
    "Saraswati",
    "Narmada",
    "Sindhu",
    "Kaveri",
  ];

  // Data for temples
  const temples = [
    { name: "Tirupati Balaji", location: "Andhra Pradesh" },
    { name: "Golden Temple", location: "Amritsar" },
    { name: "Kedarnath", location: "Uttarakhand" },
    { name: "Rameswaram", location: "Tamil Nadu" },
    { name: "Jagannath Puri", location: "Odisha" },
    { name: "Vaishno Devi", location: "Jammu & Kashmir" },
    { name: "Meenakshi Temple", location: "Tamil Nadu" },
    { name: "Shirdi Saibaba", location: "Maharashtra" },
  ];

  // Data for classical dances
  const classicalDances = [
    "Bharatanatyam",
    "Kathak",
    "Kathakali",
    "Kuchipudi",
    "Odissi",
    "Mohiniyattam",
  ];

  // Data for folk dances
  const folkDances = [
    { name: "Bhangra", region: "Punjab" },
    { name: "Garba & Dandiya", region: "Gujarat" },
    { name: "Lavani", region: "Maharashtra" },
    { name: "Ghoomar", region: "Rajasthan" },
    { name: "Yakshagana", region: "Karnataka" },
    { name: "Chhau", region: "Eastern India" },
    { name: "Bihu", region: "Assam" },
  ];

  // Stats data
  const stats = [
    { number: "50K+", label: "Community Members", icon: "👥" },
    { number: "100+", label: "Countries Connected", icon: "🌍" },
    { number: "1000+", label: "Events Organized", icon: "🎉" },
    { number: "24/7", label: "Community Support", icon: "💬" },
  ];

  return (
    <>
      {/* ══════════════════════════════════════════════
          SEO — HELMET
      ══════════════════════════════════════════════ */}
      <Helmet>
        <html lang="en" />
        <title>
          About Us – Bharatabroad | Connecting the Global Indian Diaspora
        </title>
        <meta
          name="description"
          content="Bharatabroad is a community driven platform dedicated to supporting and connecting the global Bharat (India) diaspora. Stay connected to your roots while thriving abroad."
        />
        <meta
          name="keywords"
          content="Indian diaspora, NRI community, Bharat abroad, Indian expats, global Indian community, Indian culture, Indian heritage"
        />
        <meta name="author" content="Bharatabroad" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.bharatabroad.com/about" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="About Us – Bharatabroad | Connecting the Global Indian Diaspora"
        />
        <meta
          property="og:description"
          content="Bharatabroad is a community driven platform dedicated to supporting and connecting the global Bharat (India) diaspora."
        />
        <meta property="og:url" content="https://www.bharatabroad.com/about" />
        <meta property="og:site_name" content="Bharat Abroad" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@BharatAbroad" />
        <meta name="twitter:title" content="About Us – Bharatabroad" />
        <meta
          name="twitter:description"
          content="Connecting the global Indian diaspora to roots, culture, and community."
        />

        {/* JSON-LD: Organization */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Bharatabroad",
            "url": "https://www.bharatabroad.com",
            "logo": "https://www.bharatabroad.com/logo.png",
            "sameAs": [
              "https://www.facebook.com/bharatabroad",
              "https://twitter.com/bharatabroad",
              "https://www.instagram.com/bharatabroad"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "info@bharatabroad.com",
              "contactType": "customer service",
              "availableLanguage": ["English", "Hindi"]
            },
            "description": "Community driven platform for the global Indian diaspora"
          }
        `}</script>

        {/* JSON-LD: BreadcrumbList */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.bharatabroad.com" },
              { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://www.bharatabroad.com/about" }
            ]
          }
        `}</script>
      </Helmet>

      <AppNavbar />

      <div style={{ background: "#f4f7fb" }}>
        {/* ══════════════════════════════
            HERO SECTION
        ══════════════════════════════ */}
        <div
          style={{
            background: "linear-gradient(135deg, #1a0a00, #2d1200, #1a1a00)",
            color: "white",
            padding: "80px 0",
          }}
        >
          <Container>
            <Badge bg="warning" text="dark" className="mb-3">
              Connecting the Global Bharat Nationals
            </Badge>
            <h1 className="fw-bold" style={{ fontSize: "3.5rem" }}>
              About Us
            </h1>
            <p
              className="lead mt-3"
              style={{ maxWidth: "800px", fontSize: "1.3rem" }}
            >
              Where the spirit, culture, and unity of Bharat connect the world.
            </p>
          </Container>
        </div>

        {/* ══════════════════════════════
            MISSION SECTION
        ══════════════════════════════ */}
        <section style={{ padding: "60px 0" }}>
          <Container>
            <Row className="justify-content-center">
              <Col lg={10}>
                <div style={{ textAlign: "center", marginBottom: "40px" }}>
                  <Badge bg="danger" className="mb-3">
                    🎯 Our Mission
                  </Badge>
                  <h2 style={{ color: "#1a0a00", marginBottom: "20px" }}>
                    Bridging Bharat & The World
                  </h2>
                </div>
                <p
                  style={{
                    fontSize: "1.1rem",
                    lineHeight: "1.8",
                    color: "#333",
                    textAlign: "center",
                  }}
                >
                  Bharatabroad is a community driven platform dedicated to
                  supporting and connecting the global Bharat Nationals. Our
                  mission is to serve as a trusted gateway for Bharat nationals
                  living across the world, helping them stay deeply connected to
                  their roots while thriving in their adopted countries.
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        {/* ══════════════════════════════
            WHAT WE OFFER
        ══════════════════════════════ */}
        <section
          style={{
            padding: "60px 0",
            background: "linear-gradient(135deg, #1a0a00, #2d1200)",
            color: "#fff",
          }}
        >
          <Container>
            <Row className="justify-content-center">
              <Col lg={10}>
                <div style={{ textAlign: "center", marginBottom: "30px" }}>
                  <Badge bg="light" text="dark" className="mb-3">
                    🌟 What Bharatabroad Offers
                  </Badge>
                  <h2 style={{ color: "#ffd700" }}>
                    Your Trusted Companion Abroad
                  </h2>
                </div>
                <p
                  style={{
                    fontSize: "1.05rem",
                    lineHeight: "1.8",
                    textAlign: "center",
                  }}
                >
                  Bharatabroad is designed to make life abroad more connected,
                  informed, and fulfilling. Through our platform, members can
                  discover local and global events, access valuable resources,
                  stay updated on developments related to Bharat, and engage
                  with others who share similar journeys. Whether you are a
                  student, professional, entrepreneur, or family navigating life
                  overseas, Bharatabroad aims to be a trusted companion.
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        {/* ══════════════════════════════
            COMMUNITY & VISION
        ══════════════════════════════ */}
        <section style={{ padding: "60px 0", background: "#fff" }}>
          <Container>
            <Row>
              <Col lg={6} className="mb-4">
                <div style={{ padding: "20px" }}>
                  <div style={{ fontSize: "2rem", marginBottom: "15px" }}>
                    🤝
                  </div>
                  <h3 style={{ color: "#1a0a00", marginBottom: "15px" }}>
                    Community Collaboration
                  </h3>
                  <p style={{ lineHeight: "1.7", color: "#555" }}>
                    Community collaboration lies at the heart of everything we
                    do. As we continue to grow, we actively seek partnerships,
                    guidance, and insights from community organizations and
                    leaders worldwide. Together, we aim to responsibly share
                    knowledge, amplify meaningful voices, and create
                    opportunities that benefit Bharat nationals and the diaspora
                    at large.
                  </p>
                </div>
              </Col>
              <Col lg={6} className="mb-4">
                <div style={{ padding: "20px" }}>
                  <div style={{ fontSize: "2rem", marginBottom: "15px" }}>
                    ✨
                  </div>
                  <h3 style={{ color: "#1a0a00", marginBottom: "15px" }}>
                    Our Vision
                  </h3>
                  <p style={{ lineHeight: "1.7", color: "#555" }}>
                    To build a global network that honors tradition, celebrates
                    diversity, and fosters innovation, creating lasting
                    connections across borders. By staying rooted in our
                    heritage while embracing a global outlook, we empower each
                    other to succeed and contribute meaningfully wherever we
                    are.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* ══════════════════════════════
            CONTACT SECTION
        ══════════════════════════════ */}
        <section style={{ padding: "60px 0", background: "#f9f6f0" }}>
          <Container>
            <div
              style={{
                background: "#fff",
                borderRadius: "20px",
                padding: "50px 30px",
                textAlign: "center",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                border: "1px solid #e0d5c0",
              }}
            >
              <div style={{ fontSize: "3rem", marginBottom: "15px" }}>📧</div>
              <h2 style={{ color: "#1a0a00", marginBottom: "15px" }}>
                Contact Us
              </h2>
              <p
                style={{
                  color: "#666",
                  maxWidth: "500px",
                  margin: "0 auto 25px",
                }}
              >
                Have questions, suggestions, or want to collaborate with us?
                Please Email us
              </p>
              <a
                href="mailto:info@bharatabroad.com"
                style={{
                  background: "linear-gradient(135deg, #1a0a00, #2d1200)",
                  color: "#fff",
                  padding: "14px 35px",
                  borderRadius: "50px",
                  textDecoration: "none",
                  fontSize: "1.2rem",
                  fontWeight: "500",
                  display: "inline-block",
                  transition: "all 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.boxShadow =
                    "0 5px 15px rgba(0,0,0,0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                info@bharatabroad.com
              </a>
            </div>
          </Container>
        </section>

        {/* ══════════════════════════════
            FOOTER QUOTE
        ══════════════════════════════ */}
        <section style={{ padding: "40px 0", background: "#1a0a00" }}>
          <Container>
            <p
              style={{
                textAlign: "center",
                fontSize: "1.3rem",
                color: "#ffd700",
                fontStyle: "italic",
                margin: 0,
              }}
            >
              "Where the spirit, culture, and unity of Bharat connect the
              world."
            </p>
          </Container>
        </section>
      </div>
    </>
  );
};

export default AboutUs;
